import { makeStyles, MenuItem, Select, Tooltip } from '@material-ui/core'
import { Update } from '@material-ui/icons'
import jsonexport from 'jsonexport/dist'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Datagrid,
  ExportButton,
  ResourceContextProvider,
  Show,
  TextField,
  TopToolbar,
  useTranslate,
  sanitizeListRestProps,
  downloadCSV,
  ListActionsProps,
  useListContext,
  useShowContext,
  List,
} from 'react-admin'
import { useHistory, useLocation } from 'react-router'
import { DynamicStatisticDto } from '../../../core/dto/dynamic-statistic.dto'
import { ResourceName } from '../../../core/ResourceName'

const useStyles = makeStyles((theme) => ({
  timer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  select: {
    '& > div': {
      padding: theme.spacing(1),
    },
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

const ViewTitle = ({ ...props }) => {
  const { record } = props
  const t = useTranslate()
  return (
    <span>
      {t(`resources.${ResourceName.STATS_DYNAMIC_STATISTICS}.titles.show`)}:{' '}
      {record?.name}
    </span>
  )
}

const DynamicStatisticsGrid = ({ ...props }) => {
  const { data, ids, loaded, loading } = props

  const gridColumns = useMemo(
    () =>
      !loading &&
      loaded &&
      Object.keys(data[ids[0]]).map(
        (key: string) =>
          key !== 'id' && (
            <TextField source={key} label={key} key={key} sortable={false} />
          ),
      ),
    [data, ids, loading, loaded],
  )

  return <Datagrid {...props}>{gridColumns}</Datagrid>
}

const StatisticsRefreshSelect = ({ ...props }) => {
  const { intervalId, intervalLength, setIntervalId, setIntervalLength } = props
  const translate = useTranslate()
  const location = useLocation()
  const history = useHistory()
  const classes = useStyles()

  const handleSetIntervalLength = (e) => {
    setIntervalLength(e?.target?.value)
    if (intervalId) {
      clearInterval(intervalId)
      setIntervalId(undefined)
    }
    if (e?.target?.value === 0) {
      const params = new URLSearchParams(location.search)
      params.delete('interval')
      history.replace(`${location.pathname}?${params.toString()}`)
    }
  }

  return (
    <div className={classes.timer}>
      <Tooltip
        title={translate('resources.stats-dynamic-statistics.fields.interval')}
      >
        <Update />
      </Tooltip>
      <Select
        className={classes.select}
        value={intervalLength}
        onChange={handleSetIntervalLength}
        variant="filled"
        label="timeout"
      >
        {[0, 10, 30, 60, 300].map((interval: number) => (
          <MenuItem key={interval} value={interval}>
            {translate(`resources.enums.TimeIntervals.${interval}`)}
          </MenuItem>
        ))}
      </Select>
    </div>
  )
}

const DynamicStatisticsViewExport = (
  statistics: any[],
  record?: DynamicStatisticDto,
) => {
  jsonexport(statistics, (err, csv) => {
    downloadCSV(csv, `${record?.name} ${new Date().toLocaleString()}`)
  })
}

export const StatisticsViewActions = (
  props: ListActionsProps & { exportButton?: boolean; refreshSelect?: boolean },
) => {
  const { exportButton, refreshSelect, ...rest } = props
  const { refetch, filterValues, total } = useListContext(rest)
  const { record } = useShowContext<DynamicStatisticDto>()
  const classes = useStyles()

  const location = useLocation()
  const params = new URLSearchParams(location?.search)
  const history = useHistory()

  const [intervalLength, setIntervalLength] = useState<number>(
    parseInt(params?.get('interval') ?? '0', 10),
  )
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(
    undefined,
  )

  useEffect(() => {
    if (!intervalId && intervalLength !== 0) {
      setIntervalId(setInterval(refetch, intervalLength * 1000))
      params.set('interval', intervalLength.toString())
      history.replace(`${location.pathname}?${params.toString()}`)
    }
    if (intervalLength === 0) {
      clearInterval(intervalId as NodeJS.Timeout)
      setIntervalId(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, intervalId, intervalLength, location, refetch, setIntervalId])

  return (
    <TopToolbar {...sanitizeListRestProps(rest)} className={classes.toolbar}>
      {refreshSelect && (
        <StatisticsRefreshSelect
          intervalId={intervalId}
          intervalLength={intervalLength}
          setIntervalId={setIntervalId}
          setIntervalLength={setIntervalLength}
        />
      )}
      {exportButton && (
        <ExportButton
          disabled={total === 0 || total >= 1000000}
          resource={ResourceName.STATS_DYNAMIC_STATISTICS_EXECUTION}
          filterValues={filterValues}
          exporter={(data) => DynamicStatisticsViewExport(data, record)}
          maxResults={1000000}
        />
      )}
    </TopToolbar>
  )
}

const DynamicStatisticsList = ({ ...props }) => {
  const { params, ...rest } = props
  const { record } = rest
  return (
    <ResourceContextProvider
      value={ResourceName.STATS_DYNAMIC_STATISTICS_EXECUTION}
    >
      <List
        actions={<StatisticsViewActions exportButton refreshSelect />}
        title=" "
        basePath={`/${ResourceName.STATS_DYNAMIC_STATISTICS}`}
        filter={{
          id: record?.id,
          dynamicallyDefinedParameters: params,
        }}
        bulkActionButtons={false}
      >
        <DynamicStatisticsGrid />
      </List>
    </ResourceContextProvider>
  )
}

export const DynamicStatisticsView = ({ ...props }) => {
  const {
    match,
    location: { search },
  } = props

  const urlSearchParams = new URLSearchParams(search)
  const params = Object.fromEntries(urlSearchParams.entries())

  return (
    <Show
      title={<ViewTitle />}
      id={match?.params?.id}
      resource={ResourceName.STATS_DYNAMIC_STATISTICS}
      basePath="/stats-dynamic-statistics"
      hasEdit={false}
      component="div"
    >
      <DynamicStatisticsList params={params} />
    </Show>
  )
}
